import { ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { HowToReg, PersonRemove } from "@mui/icons-material";
import Tooltip from "@mui/material/Tooltip";
import { baseURL, currentSeason, roles, verifiedContentUsers } from "../../Constants";
import { getPlayerMapRoles } from "../../services/PlayerRoleService";
import { getLftDivisionLabel } from "../../utils/divisionUtils";
import { RATING_KIND_VERSION, pickRating } from "../../utils/ratingDisplay";
import { RoleSummaryDto, RoleTooltipLine, buildRoleTooltipLines, computeDominantRole } from "../../models/PlayerRole";
import { fetchAccount, fetchLFTPlayers, setLFTStatus } from "../../services/AccountService";
import Loading from "../../components/Loading";
import { LFT } from "../../models/LFT";
import Filters from "./Filters";
import { PremiumBadge } from '../../components/Badges/PremiumBadge';
import { DonatorBadge } from '../../components/Badges/DonatorBadge';
import { TopDonatorBadge } from '../../components/Badges/TopDonatorBadge';
import { VerifiedBadge } from '../../components/Badges/VerifiedBadge';
import { getAllDonators, getDonationAmount } from '../../services/DonatorService';
import { Donator } from '../../models/Donator';
import { getAllBadges, PlayerBadges, isTopDonator as isTopDonatorBadge, isVerifiedContent as isVerifiedContentBadge } from '../../services/BadgeService';
import PlayerImage from '../../components/PlayerImage';
import { AuthContext } from '../../context/AuthContext';
import styles from './FreeAgentPage2.module.css';

const getTimeAgo = (dateString: string | undefined, t: (key: string, options?: Record<string, unknown>) => string): string => {
  if (!dateString) return '';
  const now = new Date();
  const date = new Date(dateString);
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  if (diffYears > 0) return `${t('lft.since')} ${t('lft.timeYears', { count: diffYears })}`;
  if (diffMonths > 0) return `${t('lft.since')} ${t('lft.timeMonths', { count: diffMonths })}`;
  if (diffDays > 0) return `${t('lft.since')} ${t('lft.timeDays', { count: diffDays })}`;
  if (diffHours > 0) return `${t('lft.since')} ${t('lft.timeHours', { count: diffHours })}`;
  return `${t('lft.since')} ${t('lft.timeJustNow')}`;
};

const parseAge = (age: unknown): number | null => {
  const parsed = parseInt(String(age), 10);
  return !isNaN(parsed) && parsed > 0 ? parsed : null;
};

// Computed-role tooltip data is fetched lazily on first hover and cached per player,
// so rendering 50 cards doesn't fire 50 requests.
const mapRolesCache = new Map<string, Promise<RoleSummaryDto[]>>();
const fetchMapRolesCached = (steamId: string): Promise<RoleSummaryDto[]> => {
  let cached = mapRolesCache.get(steamId);
  if (!cached) {
    cached = getPlayerMapRoles(steamId, currentSeason);
    mapRolesCache.set(steamId, cached);
  }
  return cached;
};

// The declared role on a card, with the same "Beregnet rolle" hover tooltip as the
// player page (dominant computed role + per-map T/CT breakdown).
const RoleWithComputedTooltip = ({ steamId, role }: { steamId: string; role: string }) => {
  const { t } = useTranslation('pages');
  const [lines, setLines] = useState<RoleTooltipLine[] | null>(null);
  const [dominant, setDominant] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const handleOpen = () => {
    if (loaded) return;
    fetchMapRolesCached(steamId).then((mapRoles) => {
      setDominant(computeDominantRole(mapRoles));
      setLines(buildRoleTooltipLines(mapRoles));
      setLoaded(true);
    });
  };

  return (
    <Tooltip
      onOpen={handleOpen}
      title={
        <div style={{ padding: '4px 2px' }}>
          <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: '6px', color: '#fff' }}>
            {t('lft.computedRole')}{dominant ? `: ${dominant}` : ''}
          </div>
          {!loaded && <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)' }}>…</div>}
          {loaded && (!lines || lines.length === 0) && (
            <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)' }}>{t('lft.computedRoleNone')}</div>
          )}
          {loaded && lines && lines.map((line, i) => (
            <div
              key={line.map}
              style={{
                display: 'flex', justifyContent: 'space-between', gap: '12px', fontSize: '0.8rem', padding: '2px 0',
                borderBottom: i < lines.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none'
              }}
            >
              <span style={{ fontWeight: 500, color: 'rgba(255,255,255,0.9)' }}>{line.map}</span>
              <span style={{ color: 'rgba(255,255,255,0.6)' }}>T {line.t} / CT {line.ct}</span>
            </div>
          ))}
        </div>
      }
      arrow
      placement="bottom"
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'rgba(30, 30, 30, 0.95)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '8px',
            padding: '8px 12px',
            maxWidth: 280,
            '& .MuiTooltip-arrow': { color: 'rgba(30, 30, 30, 0.95)' },
          },
        },
      }}
    >
      <div className={styles.cardRole} style={{ cursor: 'help' }}>{role}</div>
    </Tooltip>
  );
};

// The Free Agents page, served at /lft. Replaced the original at the season 33 launch;
// /lft2 redirects here. This is the version with the self-service "Sæt dig som ledig" toggle.
const FreeAgentPage2 = () => {
  const { t } = useTranslation(['pages', 'stats']);
  const { user, token } = useContext(AuthContext);
  const [originalPlayers, setOriginalPlayers] = useState<LFT[]>([]);
  const [players, setPlayers] = useState<LFT[]>([]);
  const [donators, setDonators] = useState<Donator[]>([]);
  const [badges, setBadges] = useState<PlayerBadges | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // "Sæt dig som ledig" modal state
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const fetchAllPlayers = useCallback(async (donatorsList: Donator[]) => {
    setLoading(true);
    try {
      const playersData = await fetchLFTPlayers();

      // Sort players by donation amount (highest first)
      // Players with 25+ donations go to the top
      const sortedPlayers = [...playersData].sort((a, b) => {
        const donationA = getDonationAmount(a.account.steamID, donatorsList);
        const donationB = getDonationAmount(b.account.steamID, donatorsList);

        if (donationA >= 25 && donationB >= 25) {
          return donationB - donationA;
        }
        if (donationA >= 25) return -1;
        if (donationB >= 25) return 1;
        return 0;
      });

      setOriginalPlayers(sortedPlayers);
      setPlayers(sortedPlayers);
    } catch (error) {
      console.error('Error fetching players:', error);
      setPlayers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      // Load donators and badges in parallel
      const [donatorsList, badgesData] = await Promise.all([
        getAllDonators(),
        getAllBadges()
      ]);
      setDonators(donatorsList);
      setBadges(badgesData);
      await fetchAllPlayers(donatorsList);
    };
    init();
  }, [fetchAllPlayers]);

  const isSelfLFT = !!user && originalPlayers.some((p) => p.account.steamID === user.steamID);

  const steamLogin = () => {
    window.location.href = `${baseURL}/Auth/steam`;
  };

  const openRoleModal = async () => {
    setSubmitError(false);
    setShowRoleModal(true);
    // Prefill the role from the user's profile if they already picked one there
    if (user) {
      try {
        const account = await fetchAccount(user.steamID);
        if (account?.role && roles.includes(account.role)) {
          setSelectedRole(account.role);
        }
      } catch {
        // Profile may not exist yet — the user just picks a role in the modal
      }
    }
  };

  const handleCtaClick = () => {
    if (!user || !token) {
      steamLogin();
      return;
    }
    if (isSelfLFT) {
      submitLFT(false);
    } else {
      openRoleModal();
    }
  };

  const submitLFT = async (lft: boolean) => {
    if (!token) return;
    setSubmitting(true);
    setSubmitError(false);
    try {
      await setLFTStatus(lft, lft ? selectedRole : undefined, token);
      setShowRoleModal(false);
      // Backend refreshed its LFT cache before responding, so refetch shows the change
      await fetchAllPlayers(donators);
    } catch (error) {
      console.error('Error updating LFT status:', error);
      setSubmitError(true);
    } finally {
      setSubmitting(false);
    }
  };

  const renderStat = (value: ReactNode, label: string, accent = false) => (
    <div>
      <div className={`${styles.statValue} ${accent ? styles.statValueAccent : ''}`}>
        {value ?? '–'}
      </div>
      <div className={styles.statLabel}>{label}</div>
    </div>
  );

  // Rating 3.0 where we have it, else 2.1, else 2.0. Cards show a player's LATEST season, which
  // for an inactive free agent can be years before R3 existed — hence the asterisk rather than a
  // versioned label: the four-column stat grid has no room for "RATING 3.0".
  const renderRating = (stats: LFT['stats']) => {
    const picked = pickRating(stats);
    if (!picked) return null;
    return (
      <>
        {picked.value.toFixed(2)}
        {picked.kind !== 'r3' && (
          <span
            title={t('stats:ratingFallbackNote', { version: RATING_KIND_VERSION[picked.kind] })}
            style={{ marginLeft: 2, fontSize: '0.7em', verticalAlign: 'super', cursor: 'help' }}
          >
            *
          </span>
        )}
      </>
    );
  };

  const renderPlayerCard = (player: LFT) => {
    const { account, stats, team } = player;
    const age = parseAge(account.age);
    const division = getLftDivisionLabel(team);
    const hasTwitter = !!account.twitter && account.twitter !== "";

    return (
      <div className={styles.card} key={account.steamID}>
        <Link to={`/player/${account.steamID}`} className={styles.cardPhoto}>
          <PlayerImage
            id={account.steamID}
            width={400}
            badges={badges ?? undefined}
            imgStyle={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top', display: 'block' }}
          />
          <div className={styles.cardBadges}>
            {badges && isTopDonatorBadge(account.steamID, badges) && <TopDonatorBadge />}
            {(badges ? isVerifiedContentBadge(account.steamID, badges) : verifiedContentUsers.includes(account.steamID)) && <VerifiedBadge />}
            {account.premium && <PremiumBadge />}
            {account.donator && <DonatorBadge />}
          </div>
          {account.lftDate && (
            <span className={styles.lftPill}>{getTimeAgo(account.lftDate, t)}</span>
          )}
          <div className={styles.cardNameOverlay}>
            <div className={styles.cardName}>
              {account.playerName}
              {age !== null && <span className={styles.cardAge}> · {age} {t('lft.years')}</span>}
            </div>
            {account.role && <RoleWithComputedTooltip steamId={account.steamID} role={account.role} />}
          </div>
        </Link>

        <div className={styles.cardStats}>
          <div className={styles.statGrid}>
            {renderStat(renderRating(stats), 'Rating', true)}
            {renderStat(stats?.kd, 'K/D')}
            {renderStat(stats ? `${Math.round(stats.kast)}%` : null, 'KAST')}
            {renderStat(account.faceitElo || null, 'ELO')}
          </div>
          {(division || stats) && (
            <div className={styles.tagRow}>
              {division && <span className={styles.tagDivision}>{division}</span>}
              {stats && <span className={styles.tagSeason}>{t('lft.season')} {stats.season}</span>}
            </div>
          )}
        </div>

        <div className={`${styles.cardFooter} ${!hasTwitter ? styles.cardFooterSingle : ''}`}>
          <a
            href={`https://steamcommunity.com/profiles/${account.steamID}`}
            target="_blank"
            rel="noreferrer"
            className={styles.steamButton}
          >
            Steam
          </a>
          {hasTwitter && (
            <a
              href={`https://twitter.com/${account.twitter}`}
              target="_blank"
              rel="noreferrer"
              className={styles.twitterButton}
            >
              Twitter
            </a>
          )}
        </div>
      </div>
    );
  };

  const renderPlayerGrid = () => {
    if (loading) {
      return (
        <div className="text-center">
          <Loading />
        </div>
      );
    }
    if (players.length === 0) {
      return (
        <p className={styles.emptyState}>
          {originalPlayers.length === 0 ? t('lft.noPlayers') : t('lft.noMatch')}
        </p>
      );
    }
    return (
      <div className={styles.grid}>
        {players.map(renderPlayerCard)}
      </div>
    );
  };


  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.title}>{t('lft.title')}</h1>
          <p className={styles.subtitle}>
            {t('lft.subtitle')} · <span className={styles.subtitleCount}>{t('lft.countAvailable', { count: players.length })}</span>
          </p>
        </div>
        <button
          className={`${styles.ctaButton} ${isSelfLFT ? styles.ctaButtonOutline : ''}`}
          onClick={handleCtaClick}
          disabled={submitting && !showRoleModal}
          title={!user ? t('lft.loginToSet') : undefined}
        >
          {isSelfLFT ? <PersonRemove sx={{ fontSize: 18 }} /> : <HowToReg sx={{ fontSize: 18 }} />}
          {isSelfLFT ? t('lft.removeLft') : t('lft.setLft')}
        </button>
      </div>

      <div className={styles.body}>
        <Filters players={originalPlayers} setPlayers={setPlayers} donators={donators} />
        <div>
          {renderPlayerGrid()}
        </div>
      </div>

      {/* Portal to body: the page wrapper's fadeUp animation (transform) would otherwise
          become the containing block for position:fixed and misplace the overlay. */}
      {showRoleModal && createPortal(
        <div className={styles.modalOverlay} onClick={() => !submitting && setShowRoleModal(false)}>
          <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
            <h2 className={styles.modalTitle}>{t('lft.chooseRole')}</h2>
            <p className={styles.modalHint}>{t('lft.chooseRoleHint')}</p>
            <div className={styles.chipRow}>
              {roles.map((role) => (
                <button
                  key={role}
                  className={`${styles.chip} ${selectedRole === role ? styles.chipActive : ''}`}
                  onClick={() => setSelectedRole(role)}
                >
                  {role}
                </button>
              ))}
            </div>
            {submitError && <p className={styles.modalError}>{t('lft.setLftError')}</p>}
            <div className={styles.modalFooter}>
              <button className={styles.cancelButton} onClick={() => setShowRoleModal(false)} disabled={submitting}>
                {t('lft.cancel')}
              </button>
              <button
                className={styles.ctaButton}
                onClick={() => submitLFT(true)}
                disabled={!selectedRole || submitting}
              >
                <HowToReg sx={{ fontSize: 18 }} />
                {t('lft.confirm')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default FreeAgentPage2;
